import { Component, ChangeDetectionStrategy, input, inject, computed } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SafeHtmlPipe } from '../../shared/safe-html-pipe';
import { NAV, CURRENT_USER, iconFor, NavCluster, NavItem } from '../nav-data';
import { AuthService } from '../../shared/auth.service';

const ADMIN_ROLE = 'ADMIN';

/** Always visible regardless of grants — the default landing page, ungated on both ends. */
const ALWAYS_VISIBLE_KEYS = new Set(['dashboard']);

@Component({
  selector: 'app-sidemenu',
  imports: [RouterLink, SafeHtmlPipe],
  templateUrl: './sidemenu.html',
  styles: [':host { display: contents; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Sidemenu {
  readonly activeKey = input<string>('');

  private readonly authService = inject(AuthService);
  protected readonly user = computed(() => this.authService.currentUser() ?? CURRENT_USER);
  protected readonly iconFor = iconFor;

  /**
   * Mirrors the backend PermissionsGuard: ADMIN bypasses every check and sees
   * the full taxonomy; everyone else sees only the modules an ADMIN actually
   * granted them (Users/Roles & Permissions), via each NavItem's `module`.
   * `hidden` items (topbar-only account pages — see NavItem's doc) are
   * excluded regardless of role; they're in `NAV` only for taxonomy
   * completeness (every route key in both `NAV` and `BREADCRUMBS`).
   */
  protected readonly filteredNav = computed<NavCluster[]>(() => {
    const isAdmin = this.authService.roleName() === ADMIN_ROLE;

    return NAV.map((cluster) => ({
      cluster: cluster.cluster,
      items: cluster.items.filter((item) => !item.hidden && (isAdmin || this.canSee(item))),
    })).filter((cluster) => cluster.items.length > 0);
  });

  private canSee(item: NavItem): boolean {
    if (ALWAYS_VISIBLE_KEYS.has(item.key)) {
      return true;
    }
    return item.module ? this.authService.hasPermission(item.module, 'R') : true;
  }
}
